import { CommonModule } from '@angular/common';
import { Component, inject, model, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { forkJoin, Observable, Subject, takeUntil } from 'rxjs';

import { AlertService } from './../../../shared/services/alert.service';

import { MatCheckboxModule } from '@angular/material/checkbox';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogModule,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { ActivatedRoute } from '@angular/router';
import { LoadingService } from '../../../shared/services/loading.service';
import { PermissionService } from '../../../shared/services/permission.service';
import { ProfileService } from '../../../shared/services/profile.service';
import { IPermission, IProfile } from '../../../utils/interfaces/profile';
import { IResponse } from '../../../utils/interfaces/response';

@Component({
  selector: 'app-profile',
  standalone: true,
  imports: [CommonModule, FormsModule, MatSlideToggleModule, MatCheckboxModule],
  templateUrl: './profile.component.html',
  styleUrl: './profile.component.scss',
})
export class ProfileComponent implements OnInit, OnDestroy {
  readonly dialog = inject(MatDialog);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly permissionService = inject(PermissionService);
  private readonly alertService = inject(AlertService);
  private readonly loadingService = inject(LoadingService);
  private readonly profileService = inject(ProfileService);
  private readonly unsubscribe$ = new Subject();

  profileId: string | null = null;
  profiles: IProfile[] = [];

  permissions: { section: string; permissions: IPermission[] }[] = [];
  permissionsName: string[] = [];
  permissionsNameCopy: string[] = [];

  ngOnInit(): void {
    const { profiles, permissions } = this.activatedRoute.snapshot.data;
    this.profiles = profiles.data;

    const permissionData: IPermission[] = permissions.data;

    permissionData.forEach((permission) => {
      let profile: { section: string; permissions: IPermission[] } = {
        section: '',
        permissions: [],
      };
      if (!this.permissions.some((p) => p.section === permission.section)) {
        profile.section = permission.section;
        profile.permissions = permissionData.filter(
          (p) => p.section === permission.section
        );
        this.permissions.push(profile);
      }
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();
  }

  sortProfilesByName(): void {
    this.profiles.sort((a, b) => (a > b ? 1 : -1));
  }

  changeStatus(index: number, profileId: string): void {
    if (profileId === '0ce77a2a-70a4-477d-9720-aada64089436') return;

    this.loadingService.showLoading();

    this.profileService
      .changeStatusProfile(profileId)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (res) => {
          this.profiles[index].removed = !this.profiles[index].removed;

          this.loadingService.closeLoading();

          this.alertService.success(res.message);
        },
      });
  }

  toggleCollapse(id: string, change = false): void {
    if (this.profileId === id && !change) {
      this.profileId = null;
      this.permissionsName = [];
      this.permissionsNameCopy = [];

      return;
    }

    this.permissionsName =
      this.profiles.find((p) => p.id === id)?.permissions.map((p) => p.name) ??
      [];

    this.permissionsNameCopy = JSON.parse(JSON.stringify(this.permissionsName));

    this.profileId = id;
  }

  openDialogNewProfile() {
    let config: any = {
      minWidth: 500,
      maxWidth: 500,
    };

    const dialogRef = this.dialog.open(EditProfile, config);

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((result) => {
        if (!result) return;

        this.loadingService.showLoading();
        this.profileService
          .createProfile(result)
          .pipe(takeUntil(this.unsubscribe$))
          .subscribe({
            next: (res) => {
              this.profiles.push({
                id: res.data,
                name: result,
                removed: false,
                permissions: [],
              });

              this.loadingService.closeLoading();
              this.alertService.success(res.message);
            },
          });
      });
  }

  openDialogEditProfile(profile: IProfile) {
    let config: any = {
      minWidth: 500,
      maxWidth: 500,
      data: {
        profileToEdit: profile.name,
      },
    };

    const dialogRef = this.dialog.open(EditProfile, config);

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((result) => {
        if (!result) return;

        this.loadingService.showLoading();
        this.profileService
          .updateProfile(profile.id, result)
          .pipe(takeUntil(this.unsubscribe$))
          .subscribe({
            next: (res) => {
              profile.name = result;

              this.loadingService.closeLoading();
              this.alertService.success(res.message);
            },
          });
      });
  }

  changePermission(per: IPermission) {
    if (this.profileId === '0ce77a2a-70a4-477d-9720-aada64089436') return;

    if (!this.permissionsName.includes(per.name))
      this.permissionsName.push(per.name);
    else {
      this.permissionsName = [
        ...this.permissionsName.filter((p) => p !== per.name),
      ];
    }
  }

  checkAllSelected(section: string): boolean {
    const permissions = this.permissions.find(
      (p) => p.section === section
    )?.permissions;
    return (
      permissions?.every((p) => this.permissionsName.includes(p.name)) ?? false
    );
  }

  addAllSectionPermissions(per: {
    section: string;
    permissions: IPermission[];
  }) {
    if (!this.checkAllSelected(per.section)) {
      this.permissionsName.push(
        ...per.permissions
          .filter((p) => !this.permissionsName.some((f) => f === p.name))
          .map((p) => p.name)
      );
    } else {
      this.permissionsName = this.permissionsName.filter(
        (p) => !per.permissions.some((f) => f.name === p)
      );
    }
  }

  save() {
    this.loadingService.showLoading();

    let toAdd: string[] = [];

    this.permissions.forEach((p) => {
      const add = p.permissions
        .filter((f) =>
          this.permissionsName
            .filter((p) => !this.permissionsNameCopy.includes(p))
            .includes(f.name)
        )
        .map((p) => p.id);

      toAdd = [...toAdd, ...add];
    });

    const toRemove = this.profiles
      .find((prof) => prof.id === this.profileId)
      ?.permissions.filter((p) =>
        this.permissionsNameCopy
          .filter((p) => !this.permissionsName.includes(p))
          .includes(p.name)
      )
      .map((p) => p.rolePermissionId);

    let OBS: Observable<IResponse<any>>[] = [];

    if (toAdd.length) {
      OBS.push(
        this.permissionService.createProfilePermission({
          idRole: this.profileId!,
          permissionList: toAdd,
        })
      );
    }

    if (toRemove?.length) {
      OBS.push(this.permissionService.deleteProfilePermission(toRemove));
    }

    forkJoin(OBS)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        this.getAllProfile();
        this.alertService.success('Permissões alteradas com sucesso');
      });
  }

  getAllProfile() {
    this.profileService
      .getAllProfile()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((res) => {
        this.profiles = [...res.data];

        this.toggleCollapse(this.profileId!, true);
        this.loadingService.closeLoading();
      });
  }

  checkChangeProfile(): boolean {
    return (
      this.permissionsNameCopy.every((p) => this.permissionsName.includes(p)) &&
      this.permissionsName.every((p) => this.permissionsNameCopy.includes(p))
    );
  }
}

@Component({
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
  ],
  template: `
    <h2 mat-dialog-title>
      {{ data && data.profileToEdit ? 'Editar perfil' : 'Cadastrar perfil' }}
    </h2>
    <mat-dialog-content class="mat-typography">
      <mat-form-field>
        <mat-label>Nome do perfil</mat-label>
        <input matInput [(ngModel)]="profile" />
      </mat-form-field>
    </mat-dialog-content>
    <mat-dialog-actions>
      <button class="btn danger__no-bg" mat-dialog-close>Cancelar</button>
      <button
        class="btn danger"
        [mat-dialog-close]="profile()"
        [disabled]="profile().length <= 3"
      >
        {{ data && data.profileToEdit ? 'Salvar' : 'Cadastrar' }}
      </button>
    </mat-dialog-actions>
  `,
  styles: [
    `
      @import '../../../shared/scss/btn.scss';
      mat-form-field {
        width: 100%;
      }

      mat-dialog-actions {
        justify-content: space-between;
      }
    `,
  ],
})
export class EditProfile {
  readonly data = inject<{ profileToEdit: string }>(MAT_DIALOG_DATA);

  profile = model(this.data?.profileToEdit ?? '');
}
